import React, { useState, useEffect } from 'react';
import { Building, User, UserPlus, X, Trash2, Save } from 'lucide-react';
import { api } from '../utils/api';

const DepartmentManagement = ({ user }) => {
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptHead, setNewDeptHead] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // ⚠️ API จะส่งกลับมาเฉพาะข้อมูลของโรงเรียนที่คุณครูสังกัดอยู่เท่านั้นแบบอัตโนมัติ
      const [deptData, empData] = await Promise.all([
        api.getDepartments(),
        api.getEmployees()
      ]);
      
      setDepartments(deptData);
      
      // กรองพนักงานที่ Active และไม่ใช่ Admin สูงสุด
      setEmployees(empData.filter(e => e.is_active == 1 && e.position !== 'ผู้ดูแลระบบ' && e.employee_id !== 'ADMIN'));
    } catch (error) {
      console.error('Error loading data:', error);
      window.alert('ไม่สามารถโหลดข้อมูลได้: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDepartment = async () => {
    if (!newDeptName) {
      window.alert('กรุณากรอกชื่อแผนก');
      return;
    }
    try {
      // ⚠️ Backend จะผูก organization_id ให้กับแผนกนี้โดยอัตโนมัติ
      await api.addDepartment({
        department_name: newDeptName,
        head_employee_id: newDeptHead || null
      });
      setShowAddModal(false);
      setNewDeptName('');
      setNewDeptHead('');
      loadData();
      window.alert('เพิ่มแผนกเรียบร้อย');
    } catch (error) {
      console.error('Error adding department:', error);
      window.alert('ไม่สามารถเพิ่มแผนกได้: ' + error.message);
    }
  };

  const handleDeleteDepartment = async (dept) => {
    if (!window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการลบแผนก "${dept.department_name}"?\n(การดำเนินการนี้จะไม่ลบตัวบุคลากรที่อยู่ในแผนก)`)) {
      return;
    }
    try {
      // ⚠️ Backend จะเช็คให้ด้วยว่าเรามีสิทธิ์ลบแผนกนี้ (ต้องเป็นของโรงเรียนเรา)
      await api.deleteDepartment(dept.id);
      loadData();
      window.alert('ลบแผนกเรียบร้อย');
    } catch (error) {
      console.error('Error deleting department:', error);
      window.alert('ไม่สามารถลบแผนกได้: ' + error.message);
    }
  };

  const handleHeadChange = async (deptId, newHeadId) => {
    try {
      await api.updateDepartmentHead(deptId, newHeadId);
      loadData();
      // ไม่ต้อง alert ทุกครั้งที่เปลี่ยน เพื่อความลื่นไหลในการใช้งาน
    } catch (error) {
      console.error('Error updating head:', error);
      window.alert('ไม่สามารถอัปเดตหัวหน้าแผนกได้: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <h1 className="text-3xl font-bold text-gray-900">จัดการแผนก/กลุ่มสาระฯ</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-500 text-white px-6 py-3 rounded-md hover:bg-blue-600 flex items-center space-x-2 transition-colors"
        >
          <UserPlus className="h-5 w-5" />
          <span>เพิ่มแผนกใหม่</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ชื่อแผนก/กลุ่มสาระฯ</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">หัวหน้าแผนก</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">การจัดการ</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {departments.map((dept) => (
                <tr key={dept.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {dept.department_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <select
                      value={dept.head_employee_id || ''}
                      onChange={(e) => handleHeadChange(dept.id, e.target.value)}
                      className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- ไม่ระบุหัวหน้า --</option>
                      {employees.map(emp => (
                        <option key={emp.employee_id} value={emp.employee_id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_id})
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleDeleteDepartment(dept)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                      title="ลบแผนก"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {departments.length === 0 && (
          <div className="text-center py-12">
            <Building className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">ไม่พบข้อมูลแผนก</h3>
            <p className="mt-1 text-sm text-gray-500">
              เริ่มต้นด้วยการเพิ่มแผนกแรก
            </p>
          </div>
        )}
      </div>

      {/* Add Department Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-6">เพิ่มแผนกใหม่</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อแผนก/กลุ่มสาระฯ *</label>
                <input
                  type="text"
                  value={newDeptName}
                  onChange={(e) => setNewDeptName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="เช่น กลุ่มสาระฯ วิทยาศาสตร์"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">หัวหน้าแผนก (เลือกภายหลังได้)</label>
                <select
                  value={newDeptHead}
                  onChange={(e) => setNewDeptHead(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- ไม่ระบุหัวหน้า --</option>
                  {employees.map(emp => (
                    <option key={emp.employee_id} value={emp.employee_id}>
                      {emp.first_name} {emp.last_name} ({emp.employee_id})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex space-x-4 mt-8">
              <button
                onClick={handleAddDepartment}
                disabled={!newDeptName}
                className="flex-1 bg-blue-500 text-white py-3 px-4 rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                บันทึก
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentManagement;